import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { multiplaiAPI } from "../lib/api";
import { Loader2, Check, AlertTriangle, Link2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function MultiplaiCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [linkedInfo, setLinkedInfo] = useState(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (!code) {
      setStatus("error");
      setError("No authorization code received from Multiplai");
      return;
    }

    const handleCallback = async () => {
      try {
        const existingToken = localStorage.getItem("usg_token");

        if (existingToken) {
          await multiplaiAPI.connect(code);
          toast.success("Multiplai account connected!");
          window.location.href = "/settings";
          return;
        }

        const res = await multiplaiAPI.callback(code, state);

        localStorage.setItem("usg_token", res.data.token);
        localStorage.setItem("usg_user", JSON.stringify(res.data.user));

        if (res.data.newly_linked) {
          setLinkedInfo(res.data.user);
          setStatus("linked");
          setTimeout(() => { window.location.href = "/dashboard"; }, 3000);
        } else {
          toast.success(`Welcome, ${res.data.user.name}!`);
          setStatus("success");
          setTimeout(() => { window.location.href = "/dashboard"; }, 1000);
        }
      } catch (err) {
        setStatus("error");
        setError(err.response?.data?.detail || "SSO authentication failed");
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md text-center shadow-sm">
        <div className="flex items-center gap-3 justify-center mb-6">
          <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-heading font-bold text-lg">U</span>
          </div>
          <span className="font-heading font-bold text-lg text-slate-900">USG</span>
        </div>

        {status === "loading" && (
          <div className="space-y-3">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto" />
            <p className="text-slate-500 font-medium">Authenticating with Multiplai...</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-3">
            <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-900">Welcome!</h2>
            <p className="text-sm text-slate-500">Redirecting to dashboard...</p>
          </div>
        )}

        {status === "linked" && linkedInfo && (
          <div className="space-y-4" data-testid="account-linked-notice">
            <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto">
              <Link2 className="w-7 h-7 text-indigo-600" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-900">Account Linked</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Your email <strong className="text-slate-900">{linkedInfo.email}</strong> already had an existing account. We've linked your Multiplai account so you can sign in with either method going forward.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 justify-center">
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Multiplai linked successfully</span>
            </div>
            <p className="text-xs text-slate-400">Redirecting to dashboard...</p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-3">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-900">Connection Failed</h2>
            <p className="text-sm text-red-500">{error}</p>
            <Button onClick={() => navigate("/login")} className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white">
              Go to Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
